import type { ImpersonateLogsListParams, ImpersonateLogsSortKey, ImpersonateLogsImpersonateLogsFiltersProps } from '../types/pages'
/**
 * Impersonate Logs — React parity with Angular `ReportsImpersonateComponent`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageShell } from '@/components/layout/PageShell'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ROUTE_BUILDERS } from '@/config/routes'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError } from '@/lib/utils/notifications'
import {
  ReportDataTable,
  type ReportDataTableColumn,
} from '../components/ReportDataTable'
import { ReportDateRangePicker } from '../components/ReportDateRangePicker'
import { getImpersonateLogList } from '../services/reportsService'
import type { ImpersonateLogListResponse, ImpersonateLogRow } from '../types/api'
import {
  buildImpersonateLogsSessionFromParams,
  clearImpersonateLogsSession,
  readImpersonateLogsSession,
  sessionFilterToImpersonateLogsPatch,
  writeImpersonateLogsSession,
} from '../utils/impersonateLogsSessionFilter'
import { ymdToDmy } from '../utils/reportDateParams'
import { DEFAULT_IMPERSONATE_LOGS_PARAMS as DEFAULT_PARAMS } from '../constants/pages/impersonateLogsPage'
import { LABEL_CLASS } from '../constants/configuredReportStyles'

function initialParams(): ImpersonateLogsListParams {
  const session = readImpersonateLogsSession()
  if (!session) return { ...DEFAULT_PARAMS }
  return { ...DEFAULT_PARAMS, ...sessionFilterToImpersonateLogsPatch(session) }
}

function buildImpersonateLogQueryParams(p: ImpersonateLogsListParams): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  const fromDt = ymdToDmy(p.fromDt)
  const toDt = ymdToDmy(p.toDt)
  if (fromDt) qs.set('fromDt', fromDt)
  if (toDt) qs.set('toDt', toDt)
  return qs
}

function countActiveFilters(p: ImpersonateLogsListParams): number {
  let count = 0
  if (p.q.trim()) count++
  if (p.fromDt || p.toDt) count++
  return count
}

function padApplicantId(value: number | undefined): string {
  if (value == null) return ''
  return String(value).padStart(8, '0')
}

function formatDisplayDate(value: string | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  }
  return String(value)
}

function sanitizeLegacyHtml(value: string | undefined): string {
  if (!value) return '—'
  return String(value)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '')
}


function ImpersonateLogsFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: ImpersonateLogsImpersonateLogsFiltersProps) {
  const [draft, setDraft] = useState<ImpersonateLogsListParams>(filters)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: re-sync the filter draft to applied filters each time the drawer opens
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix="impersonate-logs"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Edited Date</label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) =>
              setDraft((prev) => ({
                ...prev,
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              }))
            }
          />
        </div>
      </div>
    </ListFilterDrawer>
  )
}

export function ImpersonateLogsPage() {
  const { hasWriteAccess } = useUserRole()
  const canOpenApplication = hasWriteAccess('application')
  const [params, setParams] = useState<ImpersonateLogsListParams>(initialParams)
  const [searchDraft, setSearchDraft] = useState(() => params.q)
  const [rows, setRows] = useState<ImpersonateLogRow[]>([])
  const [pageOptions, setPageOptions] = useState<
    Pick<ImpersonateLogListResponse, 'current' | 'last' | 'total'>
  >({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    setSearchDraft(params.q)
  }, [params.q])

  const load = useCallback(async (next: ImpersonateLogsListParams) => {
    setLoading(true)
    try {
      const res = await getImpersonateLogList(
        buildImpersonateLogQueryParams(next),
      )
      setRows(res.data ?? [])
      setPageOptions({
        current: res.current ?? 1,
        last: Math.max(1, res.last ?? 1),
        total: res.total ?? 0,
      })
      writeImpersonateLogsSession(buildImpersonateLogsSessionFromParams(next))
    } catch (err) {
      showApiError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(params)
  }, [load, params])

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = searchDraft.trim()
      if (q !== params.q.trim()) {
        setParams((prev) => ({ ...prev, q, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, params.q])

  const sort = (orderBy: ImpersonateLogsSortKey) => {
    setParams((prev) => ({
      ...prev,
      orderBy,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      page: 1,
    }))
  }

  const resetFilters = () => {
    setSearchDraft('')
    clearImpersonateLogsSession()
    setParams({ ...DEFAULT_PARAMS })
  }

  const columns = useMemo<ReportDataTableColumn<ImpersonateLogRow, ImpersonateLogsSortKey>[]>(
    () => [
      {
        id: 'impersonated_by',
        label: 'Liberty Staff',
        sortKey: 'impersonated_by',
        render: (row) => row.impersonated_by || '—',
      },
      {
        id: 'fk_applicant_id',
        label: 'Applicant',
        sortKey: 'fk_applicant_id',
        render: (row) => {
          const applicantId = row.fk_applicant_id
          const padded = padApplicantId(applicantId)
          if (applicantId != null && canOpenApplication) {
            return (
              <Link
                to={ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(applicantId)}
                className="text-[#007BFF] hover:underline"
              >
                {padded}
              </Link>
            )
          }
          return padded || '—'
        },
      },
      {
        id: 'page',
        label: 'Page',
        sortKey: 'page',
        render: (row) => row.page || '—',
      },
      {
        id: 'notes',
        label: 'Section',
        sortKey: 'notes',
        className: 'max-w-[20rem] whitespace-normal',
        render: (row) => (
          <span
            dangerouslySetInnerHTML={{ __html: sanitizeLegacyHtml(row.notes) }}
          />
        ),
      },
      {
        id: 'history',
        label: 'Edited Fields',
        sortKey: 'history',
        className: 'max-w-[28rem] whitespace-normal',
        render: (row) => (
          <span
            dangerouslySetInnerHTML={{
              __html: sanitizeLegacyHtml(row.history),
            }}
          />
        ),
      },
      {
        id: 'created_at',
        label: 'Edited Date',
        sortKey: 'created_at',
        render: (row) => formatDisplayDate(row.created_at),
      },
    ],
    [canOpenApplication],
  )

  return (
    <div data-testid="impersonate-logs-page">
      <PageShell
        title="Impersonate Logs"
        pageHeaderTestIdRoot="impersonate-logs-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder="Search.."
          onSearchChange={setSearchDraft}
          activeFilterCount={countActiveFilters(params)}
          onOpenFilters={() => setFiltersOpen(true)}
          testIdPrefix="impersonate-logs"
        />

        <ImpersonateLogsFilters
          filters={params}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => setParams({ ...next, page: 1 })}
          onReset={resetFilters}
        />

        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="impersonate-logs"
            summaryLayout="angular"
          />

          <ReportDataTable
            rows={rows}
            columns={columns}
            loading={loading}
            orderBy={params.orderBy}
            sortBy={params.sortBy}
            testIdPrefix="impersonate-logs"
            rowKey={(row, index) =>
              row.id ?? `${row.fk_applicant_id ?? 'row'}-${index}`
            }
            onSort={sort}
          />

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="impersonate-logs"
            summaryLayout="angular"
          />
        </div>
      </PageShell>
    </div>
  )
}
